import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

import { FolderType } from '@/api/generated';
import HierarchyFolderIcon from '@/assets/icons/hierarchy-inline-folder.svg?react';
import HierarchySubFolderIcon from '@/assets/icons/hierarchy-inline-subfolder.svg?react';
import HierarchySubOrgIcon from '@/assets/icons/hierarchy-inline-suborg.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import DropdownMenu, { DropdownTriggerMode } from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { buildPathToLocation, getRelevantParentFolder } from '@/components/fields/Location/locationTreeUtils';
import { useFolderPermissions } from '@/hooks/permissions/useFolderPermissions';
import { useOrganizationPermissions } from '@/hooks/permissions/useOrganizationPermissions';
import { useSuborganizationPermissions } from '@/hooks/permissions/useSuborganizationPermissions';
import { useDirection } from '@/hooks/useDirection';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import typography from '@/theme/typography';

import { GetOrganizationItemsRequestParams } from './defaults';

interface NewItemButtonProps {
  queryBody: GetOrganizationItemsRequestParams | null;
  locationSlug?: string | null;
  organizationSlug?: string;
  isEnterprise?: boolean;
  onOpenModal: (type: FolderType) => void;
  locationType?: FolderType;
}

interface RestrictionRule {
  allowedParentTypes?: FolderType[];
  maxPathCount?: number;
  maxChildrenCount?: number;
}

interface ActionItem {
  type: FolderType;
  count: number;
  node: React.ReactNode;
}

const RESTRICTIONS_MAP_ENTERPRISE: {
  [key: string]: RestrictionRule;
} = {
  [FolderType.SUB_ORGANIZATION]: {
    allowedParentTypes: [FolderType.ORGANIZATION],
    maxPathCount: 1
  },
  [FolderType.FOLDER]: {
    allowedParentTypes: [FolderType.FOLDER, FolderType.SUB_ORGANIZATION],
    maxPathCount: 1
  },
  [FolderType.SUB_FOLDER]: {
    allowedParentTypes: [FolderType.FOLDER, FolderType.SUB_FOLDER],
    maxPathCount: 5
  }
};

const RESTRICTIONS_MAP_REGULAR: {
  [key: string]: RestrictionRule;
} = {
  [FolderType.FOLDER]: {
    allowedParentTypes: [FolderType.FOLDER, FolderType.ORGANIZATION],
    maxPathCount: 1
  },
  [FolderType.SUB_FOLDER]: {
    allowedParentTypes: [FolderType.FOLDER, FolderType.SUB_FOLDER],
    maxPathCount: 5
  }
};

const NewItemButton: FC<NewItemButtonProps> = ({
  locationSlug,
  organizationSlug,
  isEnterprise,
  onOpenModal,
  locationType
}) => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['organizations', 'common']);
  const { end } = useDirection();
  const onCreateSubOrg = useCallback(() => onOpenModal(FolderType.SUB_ORGANIZATION), [onOpenModal]);
  const onCreateFolder = useCallback(() => onOpenModal(FolderType.FOLDER), [onOpenModal]);
  const onCreateSubFolder = useCallback(() => onOpenModal(FolderType.SUB_FOLDER), [onOpenModal]);

  const { mapStructure, location } = useOrganizationStructure(locationSlug!);

  const relevantParentFolder = useMemo(
    () => getRelevantParentFolder(location!, mapStructure),
    [location, mapStructure]
  );

  const { value: { canCreateSuborganizations, canCreateFolders: canCreateFoldersOrganization } = {} } =
    useOrganizationPermissions({
      variables: { organizationSlug }
    });
  const { value: { canCreateFolders: canCreateFoldersSuborganization } = {} } = useSuborganizationPermissions({
    variables: {
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType
    }
  });

  const { value: { canCreateSubfolders } = {} } = useFolderPermissions({
    variables: {
      organizationSlug,
      locationSlug: relevantParentFolder?.slug,
      locationType: relevantParentFolder?.type
    }
  });

  const path = useMemo(
    () => (locationSlug ? buildPathToLocation(mapStructure, locationSlug) : []),
    [mapStructure, locationSlug]
  );
  const leafType = path?.[path?.length - 1]?.type;
  const leafChildrenCount = path?.[path?.length - 1]?.children?.length;

  const subOrgCount = useMemo(() => path.filter(item => item.type === FolderType.SUB_ORGANIZATION)?.length, [path]);
  const folderCount = useMemo(() => path.filter(item => item.type === FolderType.FOLDER)?.length, [path]);
  const subFolderCount = useMemo(() => path.filter(item => item.type === FolderType.SUB_FOLDER)?.length, [path]);

  const validateAllowedItemType = useCallback(
    (
      newType?: FolderType,
      leafType?: FolderType,
      pathCount?: number,
      leafChildrenCount?: number,
      isEnterprise?: boolean
    ) => {
      const restrictionsMap = isEnterprise ? RESTRICTIONS_MAP_ENTERPRISE : RESTRICTIONS_MAP_REGULAR;
      const currentRestriction = restrictionsMap?.[newType as string];
      if (!currentRestriction) return false;
      if ((pathCount ?? 0) + 1 > (currentRestriction?.maxPathCount ?? Infinity)) return false;
      if ((leafChildrenCount ?? 0) + 1 > (currentRestriction?.maxChildrenCount ?? Infinity)) return false;
      if (!currentRestriction?.allowedParentTypes?.includes(leafType!)) return false;

      return true;
    },
    []
  );

  const availableActions: ActionItem[] = useMemo(
    () =>
      (
        [
          canCreateSuborganizations && {
            type: FolderType.SUB_ORGANIZATION,
            count: subOrgCount,
            node: (
              <DropdownMenuItem
                text={t('layouts.organizations.create.items.suborganization')}
                icon={<HierarchySubOrgIcon />}
                onClick={onCreateSubOrg}
              />
            )
          },
          (canCreateFoldersOrganization || canCreateFoldersSuborganization) && {
            type: FolderType.FOLDER,
            count: folderCount,
            node: (
              <DropdownMenuItem
                text={t('layouts.organizations.create.items.folder')}
                icon={<HierarchyFolderIcon />}
                onClick={onCreateFolder}
              />
            )
          },
          canCreateSubfolders && {
            type: FolderType.SUB_FOLDER,
            count: subFolderCount,
            node: (
              <DropdownMenuItem
                text={t('layouts.organizations.create.items.subfolder')}
                icon={<HierarchySubFolderIcon />}
                onClick={onCreateSubFolder}
              />
            )
          }
        ] as (ActionItem | false)[]
      ).filter(
        (actionItem: ActionItem | false): actionItem is ActionItem =>
          !!actionItem &&
          validateAllowedItemType(actionItem?.type, leafType, actionItem?.count, leafChildrenCount, isEnterprise)
      ),
    [
      canCreateSuborganizations,
      subOrgCount,
      t,
      onCreateSubOrg,
      canCreateFoldersOrganization,
      canCreateFoldersSuborganization,
      folderCount,
      onCreateFolder,
      canCreateSubfolders,
      subFolderCount,
      onCreateSubFolder,
      validateAllowedItemType,
      leafType,
      leafChildrenCount,
      isEnterprise
    ]
  );

  return (
    <>
      <DropdownMenu
        title={t('common:layouts.filters.new')}
        triggerMode={DropdownTriggerMode.TEXT_BUTTON}
        menuProps={{
          transformOrigin: {
            vertical: 'top',
            horizontal: end
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: end
          }
        }}
        buttonProps={{
          disabled: !availableActions?.length,
          variant: 'contained',
          startIcon: <PlusIcon className="PlusIcon-root" />,
          sx: {
            ...typography.body2,
            fontWeight: typography.fontWeightMedium,
            pb: 0.5,
            pt: 0.5,
            paddingInlineStart: 1,
            paddingInlineEnd: 1.5,
            '& .MuiButton-icon': {
              marginInlineStart: 0,
              marginInlineEnd: 1
            },
            '& .PlusIcon-root': { width: spacing(2), height: spacing(2) }
          }
        }}
        autoClose
      >
        {availableActions?.map(actionItem => actionItem?.node)}
      </DropdownMenu>
    </>
  );
};

export default NewItemButton;
